import posthog from 'posthog-js';

/**
 * PostHog (EU Cloud) integration for Confide.
 *
 * Confide is a whistleblower platform. The integration is intentionally
 * paranoid — see `docs/29-analytics-posthog.md` for the full design notes.
 *
 * Key privacy invariants enforced here:
 * - Anonymous only: `person_profiles: 'identified_only'` + we never call `identify()`.
 * - Consent-gated: `opt_out_capturing_by_default: true` — no events leave the
 *   browser until the app explicitly calls `posthog.opt_in_capturing()`.
 * - No cross-session correlation: `persistence: 'memory'` means a new
 *   tab/reload yields a new anonymous distinct_id.
 * - No element text in autocapture: `mask_all_text: true` strips `$el_text`.
 * - No session replay (Sentry already covers that at 10%).
 * - Reporter routes never capture: `before_send` drops events whose
 *   `$current_url` matches a known reporter path, AND scrubs any
 *   referrer-shaped property that points at a reporter route.
 * - `/flags` does not phone home on first load (`advanced_disable_feature_flags_on_first_load`)
 *   — callers must explicitly `posthog.reloadFeatureFlags()` after the user
 *   has left any reporter route.
 *
 * Init is gated on `VITE_POSTHOG_KEY` being set AND `VITE_NODE_ENV` being
 * `staging` or `production`. Dev/local stay silent by default.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.i.posthog.com';
const MODE = import.meta.env.VITE_NODE_ENV;
const ENABLED_ENVS = new Set(['staging', 'production']);

/**
 * Path prefixes where PostHog must not capture *anything*, even after consent,
 * and which must be scrubbed from any referrer-shaped property on non-reporter
 * events. These are the whistleblower flows.
 */
const REPORTER_PATH_PREFIXES = [
  '/new-report',
  '/report/',
  '/customer/reports/create-report',
];

/**
 * Properties that, if present, must be inspected for reporter-route values
 * and stripped. Some are URLs, some are pathnames — `pathnameOf` handles both.
 */
const REFERRER_SHAPED_PROPS = [
  '$referrer',
  '$initial_referrer',
  '$prev_pageview_url',
  '$prev_pageview_pathname',
  '$initial_current_url',
  '$initial_pathname',
] as const;

const isReporterPath = (pathname: string): boolean =>
  REPORTER_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Extract a pathname from a value that may be a full URL or already a path.
 * Returns `null` if the value is not a usable string.
 */
const pathnameOf = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || !raw) return null;
  if (raw.startsWith('/')) return raw;
  try {
    return new URL(raw).pathname;
  } catch {
    return null;
  }
};

/**
 * Whether PostHog is initialized in this environment. Re-evaluated per call
 * so consumers don't capture stale module-load state during tests.
 */
export const isPostHogEnabled = (): boolean =>
  Boolean(KEY) && ENABLED_ENVS.has(MODE);

/**
 * Initialize PostHog. Called once from `src/main.tsx` before React renders.
 * No-op when the SDK is not enabled for this environment.
 *
 * After init, the SDK is opted *out* of capturing by default. The app must
 * call `posthog.opt_in_capturing()` once explicit consent is granted — wire
 * this to the cookie/consent banner.
 */
export const initPostHog = () => {
  if (!isPostHogEnabled()) return;
  // KEY is guaranteed truthy by isPostHogEnabled().
  posthog.init(KEY as string, {
    api_host: HOST,
    // Anonymous-only: events flow, but no person profiles are created.
    person_profiles: 'identified_only',
    // No persistent distinct_id — fresh anonymous id per tab/reload.
    persistence: 'memory',
    // Consent gate. Wire `posthog.opt_in_capturing()` to the consent banner.
    opt_out_capturing_by_default: true,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: {
      element_attribute_ignorelist: ['data-pii', 'data-confidential'],
    },
    // Strips `$el_text` from autocapture events so dynamically rendered
    // button/link labels do not leak user-supplied content.
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_session_recording: true,
    // The `/flags` endpoint phones home with the current URL on init even
    // when opt_out_capturing_by_default is true. Defer all flag evaluation
    // so a user landing directly on /new-report doesn't leak that URL.
    // Callers must explicitly `posthog.reloadFeatureFlags()` once on a safe route.
    advanced_disable_feature_flags_on_first_load: true,
    // Attack-surface reduction: features we don't use and don't want to
    // wake up on a posthog-js upgrade default-flip.
    disable_surveys: true,
    capture_dead_clicks: false,
    respect_dnt: true,
    // Defense-in-depth: drop high-risk auto-properties at SDK level.
    // `before_send` is the primary filter; this just ensures these values
    // never even reach `before_send` to be inspected.
    property_denylist: [
      '$referring_domain',
      '$initial_referring_domain',
      '$ip',
    ],
    // IP discard is also enforced project-side (PostHog EU Cloud disables
    // IP capture by default).
    before_send: (event) => {
      if (!event) return null;
      const props = event.properties ?? {};

      // Drop the event entirely if it originated on a reporter route.
      // Prefer the SDK-provided $current_url; fall back to window.location
      // for custom events fired before the first $pageview (which would
      // otherwise carry no URL property at all).
      const rawUrl =
        props.$current_url ?? globalThis.window?.location.pathname;
      const currentPath = pathnameOf(rawUrl);
      if (rawUrl && currentPath === null) {
        // Malformed URL — fail closed.
        return null;
      }
      if (currentPath && isReporterPath(currentPath)) {
        return null;
      }

      // Scrub referrer-shaped props that point at reporter routes. This
      // protects events fired on non-reporter routes whose referrer leaks
      // an earlier reporter visit.
      for (const key of REFERRER_SHAPED_PROPS) {
        const raw = props[key];
        const path = pathnameOf(raw);
        if (path && isReporterPath(path)) {
          delete props[key];
        }
      }

      return event;
    },
  });
};

/**
 * Clear the anonymous distinct_id and any cached flag state. Called from the
 * `/logout` loader so shared devices do not carry one user's session into
 * the next. No-op when PostHog is not enabled.
 */
export const resetPostHog = () => {
  if (!isPostHogEnabled()) return;
  posthog.reset();
};
